import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  input,
  output,
  viewChild,
  computed,
  effect,
  TemplateRef,
  Input,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

// dayjs plugins
import dayjs from 'dayjs';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getDateRangeFromPreset } from '@app/core/util/date_utils';
import { PresetOptionEnum } from '@app/core/constants/date_presets.enum';
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear); // dependency for isoWeeksInYear

// components
import { Popover } from 'primeng/popover';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Tooltip } from 'primeng/tooltip';
import { Ag1ButtonSmallComponent } from '../ag1-button-small/ag1-button-small.component';

export interface IDateRange {
  startDate: Date;
  endDate: Date;
}

interface IWeek {
  week: number;
  year: number;
}

enum MonthOptionEnum {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December',
}

@Component({
  selector: 'ag1-date-range',
  templateUrl: './ag1-date-range.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // add component as a value accessor to angular forms
    // this allows the component to be used as a form control
    // and to be updated by the form control
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: Ag1DateRangeComponent,
    },
  ],
  imports: [
    NgTemplateOutlet,
    Bind,
    Button,
    AngularSvgIconModule,
    Popover,
    NgSelectComponent,
    FormsModule,
    Tooltip,
    Ag1ButtonSmallComponent,
    DatePipe,
  ],
})
export class Ag1DateRangeComponent implements OnInit, ControlValueAccessor {
  private datePipe = inject(DatePipe);

  // constants
  readonly DATE_FORMAT: string = 'd MMM y' as const;

  // Outputs
  startDateChange = output<Date>();
  endDateChange = output<Date>();
  presetOptionChange = output<PresetOptionEnum>();

  // custom template
  /** Pass in a template to customize the trigger button */
  @Input() customTriggerTemplateRef?: TemplateRef<any>;

  /** Pass in a template to customize the displayed value */
  @Input() customValueTemplateRef?: TemplateRef<any>;

  /** Indicates if the date range has an error/invalid state */
  @Input() hasError: boolean = false;

  // Internal state, necessary to ensure that legacy inputs are still supported but don't override state directly
  readonly _startDate = signal<Date | null>(null);
  readonly _endDate = signal<Date | null>(null);

  // Input signals
  readonly startDate = input<Date | null>(null);
  readonly endDate = input<Date | null>(null);
  readonly clearable = input<boolean>(true);

  // display
  readonly separator = input<string>('-');
  readonly showWeekSelection = input<boolean>(true);
  readonly showPresetSelection = input<boolean>(false);
  // configures whether the user can select a single date or a range of dates
  readonly selectionType = input<'range' | 'single'>('range');

  /** When set, shown instead of the default range/single copy. */
  readonly placeholder = input<string | undefined>(undefined);

  // preset option
  readonly selectedPresetOption = input<PresetOptionEnum>();

  // use viewChild to access the menu component
  // in order to close the menu from outside the component
  readonly dateRangePickerPopover = viewChild<Popover>('dateRangePicker');

  // displayed date
  dateSelection: 'initialRange' | 'alterRange' = 'initialRange';
  weekSelection: 'initialRange' | 'alterRange' = 'initialRange';

  // selections - computed from internal date signals
  readonly selectedStartDate = computed(() => {
    const start: Date | null = this._startDate();
    return this.datePipe.transform(start, this.DATE_FORMAT) ?? '';
  });

  readonly selectedEndDate = computed(() => {
    const end: Date | null = this._endDate();
    return this.datePipe.transform(end, this.DATE_FORMAT) ?? '';
  });

  weekStartDay: number = 1; // 0 - 6, 0 being Sunday
  weekDays: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  currentMonth: string;
  months: MonthOptionEnum[] = Object.values(MonthOptionEnum);

  currentYear: number;
  years: number[];

  // calendar structure
  datesPadding: Date[] = [];
  currentDate: Date = new Date();
  readonly dates = signal<Date[]>([]);
  readonly weeks = signal<IWeek[]>([]);

  // presets
  presetOptions: PresetOptionEnum[] = [
    PresetOptionEnum.ThisWeek,
    PresetOptionEnum.Today,
    PresetOptionEnum.Yesterday,
    PresetOptionEnum.Last30Days,
    PresetOptionEnum.Last90Days,
    PresetOptionEnum.Last365Days,
  ];

  // computed signals
  readonly isRangeSelection = computed(() => this.selectionType() === 'range');
  readonly isSingleSelection = computed(
    () => this.selectionType() === 'single',
  );

  readonly displayPlaceholder = computed(() => {
    const customPlaceholder: string = this.placeholder();
    if (!customPlaceholder || customPlaceholder.trim() === '') {
      return this.isRangeSelection() ? 'Select a date range' : 'Select a date';
    }

    return customPlaceholder;
  });

  readonly dateStyles = computed(() => {
    const map = new Map<Date, string>();
    const currentDates: Date[] = this.dates();
    // Explicitly track these signals as dependencies
    this._startDate();
    this._endDate();

    currentDates.forEach((date: Date) => {
      map.set(date, this.getDateStyle(date));
    });
    return map;
  });

  constructor() {
    // Sync legacy `[startDate]` / `[endDate]` inputs into internal state. When the
    // component is used via the CVA path (`formControl` / `formControlName`),
    // `writeValue` is authoritative — these effects must not clobber it on first
    // run with `null` (the signal-input default when no `[startDate]` binding exists).
    // The `_writeValueCalled` flag distinguishes the two paths: form-driven consumers
    // get `writeValue` first, then this effect skips null pushes.
    effect(() => {
      const inputStart: Date | null = this.startDate();

      // Ignore external updates during mid-selection (date or week)
      if (
        this.dateSelection === 'alterRange' ||
        this.weekSelection === 'alterRange'
      ) {
        return;
      }

      if (!inputStart) {
        if (this._writeValueCalled) {
          return;
        }
        this._startDate.set(null);
        return;
      }

      // Convert UTC to local using helper
      this._startDate.set(this._convertUtcToLocal(inputStart));
    });

    effect(() => {
      const inputEnd: Date | null = this.endDate();

      // Ignore external updates during mid-selection (date or week)
      if (
        this.dateSelection === 'alterRange' ||
        this.weekSelection === 'alterRange'
      ) {
        return;
      }

      if (!inputEnd) {
        if (this._writeValueCalled) {
          return;
        }
        this._endDate.set(null);
        return;
      }

      // Convert UTC to local using helper
      this._endDate.set(this._convertUtcToLocal(inputEnd));
    });

    // React to preset option changes
    effect(() => {
      const selectedPresetOption: PresetOptionEnum | null =
        this.selectedPresetOption();
      if (!selectedPresetOption) {
        return;
      }
      this.selectPreset(selectedPresetOption);
    });
  }

  ngOnInit(): void {
    this.currentDate = new Date();
    this.currentMonth = this.getCurrentMonth();
    this.currentYear = this.getCurrentYear();
    this.years = this.generateYearOptions(this.currentYear);
    this.generateCalendarStructure();
  }

  // UTC conversion helper methods
  private _convertUtcToLocal(utcDate: Date | null): Date | null {
    if (!utcDate) {
      return null;
    }

    return new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds(),
      utcDate.getUTCMilliseconds(),
    );
  }

  private _convertLocalToUtc(localDate: Date | null): Date | null {
    if (!localDate) {
      return null;
    }

    return new Date(
      Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes(),
        localDate.getSeconds(),
        localDate.getMilliseconds(),
      ),
    );
  }

  getCurrentDate(): string {
    return this.datePipe.transform(this.currentDate, 'MMMM y');
  }

  getCurrentMonth(): string {
    return this.datePipe.transform(this.currentDate, 'MMMM');
  }

  getCurrentYear(): number {
    return parseInt(this.datePipe.transform(this.currentDate, 'y'));
  }

  generateYearOptions(currentYear: number): number[] {
    const yearPadding: number = 10;
    const years: number[] = [];
    for (let i = yearPadding; i >= 0; i--) {
      years.push(currentYear - i);
    }

    for (let i = 1; i <= yearPadding; i++) {
      years.push(currentYear + i);
    }
    return years;
  }

  changeCurrentYear(selectedYear: number): void {
    this.currentDate = new Date(
      selectedYear, // year
      this.currentDate.getMonth(), // month
      1, // day
      0, // hour
      0, // minute
      0, // second
      0, // millisecond
    );

    this.generateCalendarStructure();
  }

  changeCurrentMonth(selectedMonth: MonthOptionEnum): void {
    this.currentDate = new Date(
      this.currentDate.getFullYear(), // year
      this.months.indexOf(selectedMonth), // month
      1, // day
      0, // hour
      0, // minute
      0, // second
      0, // millisecond
    );

    this.generateCalendarStructure();
  }

  // calendar rendering
  setWeeks(): void {
    const currentDates: Date[] = this.dates();
    const weeksArray: IWeek[] = [];

    const firstDate: Date = currentDates[0];
    const lastDate: Date = currentDates[currentDates.length - 1];
    const firstWeek: number = dayjs(firstDate).isoWeek();
    const lastWeek: number = dayjs(lastDate).isoWeek();

    if (firstWeek < lastWeek) {
      // eg [45, 46, 47 , 48]
      for (let i = firstWeek; i <= lastWeek; i++) {
        weeksArray.push({
          week: i,
          year: dayjs(firstDate).isoWeekYear(),
        });
      }
    } else {
      // eg [52, 1, 2, 3] // this could be 53 weeks or 51 depending on the year
      // we also need to determine how many iso weeks are in the current year
      // this is to cater for different years having different number of weeks
      // as well as leap years
      for (let i = firstWeek; i <= dayjs(firstDate).isoWeeksInYear(); i++) {
        weeksArray.push({ week: i, year: dayjs(firstDate).isoWeekYear() });
      }
      for (let i = 1; i <= lastWeek; i++) {
        weeksArray.push({ week: i, year: dayjs(lastDate).isoWeekYear() });
      }
    }

    this.weeks.set(weeksArray);
  }

  generateCalendarStructure(): void {
    // gets the length of the current month
    const length: number = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() + 1,
      0,
    ).getDate();

    // returns an array of the current month's dates
    const datesArray = Array.from({ length: length }, (_elm, index) => {
      return new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth(),
        index + 1,
      );
    });

    this.dates.set(datesArray);
    this.generateWeekPlaceholders();
    this.setWeeks();
  }

  generatePreviousMonthCalendar(): void {
    // first date of previous month
    this.currentDate = new Date(
      this.currentDate.getFullYear(), // year
      this.currentDate.getMonth() - 1, // month
      1, // day
      0, // hour
      0, // minute
      0, // second
      0, // millisecond
    );

    this.currentMonth = this.getCurrentMonth();
    this.currentYear = this.getCurrentYear();

    this.generateCalendarStructure();
  }

  getFirstDayOfWeekOffset(day: Date): number {
    const currentDayOfWeek: string = this.datePipe.transform(day, 'EEEE');
    return this.weekDays.indexOf(currentDayOfWeek.substring(0, 3));
  }

  generateNextMonthCalendar(): void {
    // first date of next month
    this.currentDate = new Date(
      this.currentDate.getFullYear(), // year
      this.currentDate.getMonth() + 1, // month
      1, // day
      0, // hour
      0, // minute
      0, // second
      0, // millisecond
    );

    this.currentMonth = this.getCurrentMonth();
    this.currentYear = this.getCurrentYear();

    this.generateCalendarStructure();
  }

  generateWeekPlaceholders(): void {
    // this is needed to correctly position the days of the week
    this.datesPadding = [];

    // returns the day of the week of the first day of the month
    // 0 - 6, 0 being Sunday
    const lastDayOfWeek: number = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      0,
    ).getDay();

    // returns an array of the previous month's last days
    for (let i = lastDayOfWeek; i >= this.weekStartDay; i--) {
      this.datesPadding.push(null);
    }
  }
  // =========================================

  // date selections
  selectDate(date: Date): void {
    // ensure that there can be no conflicts between date & week selection
    this.weekSelection = 'initialRange';

    if (this.isSingleSelection()) {
      this.setStartDate(date);
      this.setEndDate(date);

      this.applyDateRange();
      return;
    }

    if (this.dateSelection === 'initialRange') {
      // initial selection
      this.setStartDate(date);
      this.setEndDate(date);
      this.dateSelection = 'alterRange';
    } else {
      // second selection alters the range
      // but becuase a user can select dates in any order

      // Only use the initial selection and the new selection
      const dates: Date[] = [date, this._startDate(), this._endDate()];

      this.orderAndSetDates(dates);
      this.dateSelection = 'initialRange';

      this.applyDateRange();
    }
  }

  selectWeek(week: IWeek): void {
    // ensure that there can be no conflicts between date & week selection
    this.dateSelection = 'initialRange';

    const workingDate: Date = new Date(week.year, 5, 1);

    // get the first and last date in the selected week
    const firstDateOfWeek: Date = dayjs(workingDate)
      .isoWeek(week.week)
      .startOf('isoWeek')
      .toDate();

    const lastDateOfWeek: Date = dayjs(workingDate)
      .isoWeek(week.week)
      .endOf('isoWeek')
      .toDate();

    if (this.weekSelection === 'initialRange') {
      // Initial selection - set both start and end to this week's range
      this.setStartDate(firstDateOfWeek);
      this.setEndDate(lastDateOfWeek);
      this.weekSelection = 'alterRange';
    } else {
      // Second selection alters the range
      // User can select weeks in any order, so we sort all dates
      const dates: Date[] = [
        this._startDate(),
        this._endDate(),
        firstDateOfWeek,
        lastDateOfWeek,
      ];
      this.orderAndSetDates(dates);
      this.weekSelection = 'initialRange';

      // Apply the final range
      this.applyDateRange();
    }
  }

  orderAndSetDates(dates: Date[]): void {
    dates.sort((a, b) => a.getTime() - b.getTime());

    const earliestDate: Date = dates[0];
    const latestDate: Date = dates[dates.length - 1];

    this.setStartDate(earliestDate);
    this.setEndDate(latestDate);
  }

  setStartDate(date: Date): void {
    // ensure that the start day has a time of 00:00:00
    this._startDate.set(
      new Date(
        date.getFullYear(), // year
        date.getMonth(), // month
        date.getDate(), // day
        0, // hour
        0, // minute
        0, // second
        0, // millisecond
      ),
    );
  }

  setEndDate(date: Date): void {
    // ensure that the end day has a time of 23:59:59
    this._endDate.set(
      new Date(
        date.getFullYear(), // year
        date.getMonth(), // month
        date.getDate(), // day
        23, // hour
        59, // minute
        59, // second
        999, // millisecond
      ),
    );
  }

  clearDates(): void {
    // reset values
    this._startDate.set(null);
    this._endDate.set(null);

    // reset selections
    this.weekSelection = 'initialRange';
    this.dateSelection = 'initialRange';

    this.applyDateRange();
  }

  compareDates(d1: Date, d2: Date): boolean {
    if (!d1 || !d2) {
      return false;
    }

    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  }
  // =========================================

  // styling
  styleToday(date: Date): boolean {
    return this.compareDates(date, new Date());
  }

  styleSelectedStartDate(date: Date): boolean {
    const start: Date | null = this._startDate();
    if (!start) return false;

    return this.compareDates(date, start);
  }

  styleSelectedEndDate(date: Date): boolean {
    const end: Date | null = this._endDate();
    if (!end) return false;

    return this.compareDates(date, end);
  }

  styleSelectedRange(date: Date): boolean {
    const start = this._startDate();
    const end = this._endDate();
    if (!start || !end) return false;

    return date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
  }

  getDateStyle(date: Date): string {
    const start: Date | null = this._startDate();
    const end: Date | null = this._endDate();

    const classes: string[] = [
      'p-1',
      'aspect-square',
      'text-center',
      'select-none',
      'cursor-pointer',
      'outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-white',
      'focus-visible:ring-interface-primary-color',
    ];

    // most specific first
    // style initial selection (i.e both start and end dates are set the same)
    if (this.compareDates(start, date) && this.compareDates(end, date)) {
      classes.push(
        'bg-interface-primary-color',
        'text-white',
        'rounded',
        'hover:bg-interface-primary-800',
      );
    } else if (this.styleSelectedEndDate(date)) {
      // style end date
      classes.push(
        'bg-interface-primary-color',
        'text-white',
        'rounded-r',
        'hover:bg-interface-primary-800',
      );
    } else if (this.styleSelectedStartDate(date)) {
      // style start date
      classes.push(
        'bg-interface-primary-color',
        'text-white',
        'rounded-l',
        'hover:bg-interface-primary-800',
      );
    } else if (this.styleSelectedRange(date)) {
      // style dates in range
      classes.push(
        'bg-interface-primary-100',
        'hover:bg-interface-primary-200',
      );
    } else if (this.styleToday(date)) {
      // style today's date
      classes.push(
        'rounded-full bg-white text-text-common-primary border border-border-common-brand hover:bg-interface-common-hover',
      );
    } else {
      // style other dates
      classes.push('rounded hover:bg-interface-common-hover');
    }

    return classes.join(' ');
  }
  // =========================================

  // events
  emitDateRangeSelection(): void {
    const start: Date | null = this._startDate();
    const end: Date | null = this._endDate();

    // Convert local to UTC using helper
    const utcStartDate: Date | null = this._convertLocalToUtc(start);
    const utcEndDate: Date | null = this._convertLocalToUtc(end);

    // for debugging (isoString is necessary to see the date in UTC)
    // console.log('startDate', utcStartDate?.toISOString());
    // console.log('endDate', utcEndDate?.toISOString());

    // emit dates
    this.startDateChange.emit(utcStartDate || null);
    this.endDateChange.emit(utcEndDate || null);
  }

  emitPresetOptionChange(): void {
    this.presetOptionChange.emit(this.selectedPresetOption());
  }

  closeMenu(): void {
    this.dateRangePickerPopover()?.hide();
  }

  handleTriggerClick(event: Event): void {
    event.stopImmediatePropagation();

    if (!this.disabled) {
      this.dateRangePickerPopover().toggle(event);
    }
  }

  selectPreset(preset: PresetOptionEnum): void {
    if (!preset) {
      return;
    }

    const { start, end } = getDateRangeFromPreset(preset);

    if (!start || !end) {
      return;
    }

    this.setStartDate(start);
    this.setEndDate(end);

    this.applyDateRange();
  }

  getPresetDisplayName(preset: PresetOptionEnum): string {
    return preset.toString();
  }

  applyDateRange(): void {
    this.markAsTouched();

    if (!this.disabled) {
      // inform parent component of date changes
      this.emitDateRangeSelection();
      this.emitPresetOptionChange();

      // update form control
      this.onChange(this.getValue());
    }

    this.closeMenu();
  }

  getValue(): IDateRange | null {
    const start: Date | null = this._startDate();
    const end: Date | null = this._endDate();
    if (!start || !end) {
      return null;
    }

    // Internal signals hold local dates; the form-control value is UTC, matching
    // the (startDateChange)/(endDateChange) outputs.
    const utcStart: Date | null = this._convertLocalToUtc(start);
    const utcEnd: Date | null = this._convertLocalToUtc(end);
    if (!utcStart || !utcEnd) {
      return null;
    }

    return { startDate: utcStart, endDate: utcEnd };
  }

  // required by ControlValueAccessor

  // ControlValueAccessor methods
  // used to update the form control
  onChange: (_value: IDateRange | null) => void = () => {};

  // used to inform the form control that the user has interacted with the component
  onTouched: () => void = () => {};

  // used to set the value using the form control
  writeValue(value: IDateRange | null): void {
    this._writeValueCalled = true;

    if (this.dateSelection === 'alterRange') {
      return;
    }

    if (value) {
      // The control value is UTC; internal signals hold local dates for display,
      // matching the [startDate]/[endDate] input effects.
      const localStart: Date | null = this._convertUtcToLocal(value.startDate);
      const localEnd: Date | null = this._convertUtcToLocal(value.endDate);
      if (this.isRangeSelection()) {
        this._startDate.set(localStart);
        this._endDate.set(localEnd);
      } else {
        this._startDate.set(localStart);
        this._endDate.set(localEnd);
      }
    } else {
      this.clearInput();
    }
  }

  // used to register the onChange function
  registerOnChange(onChange: (value: IDateRange | null) => void): void {
    this.onChange = onChange;
  }

  // used to register the onTouched function
  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  // used to set the disabled state using the form control
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  clearInput(): void {
    this._startDate.set(null);
    this._endDate.set(null);
  }

  // used to inform the form control that the user has interacted with the component
  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  // ControlValueAccessor properties
  // used to track the state of the form control
  touched: boolean = false;
  disabled: boolean = false;

  /**
   * Set to true the first time `writeValue` runs. The legacy-input effects use this to
   * stop overwriting a CVA-set value with `null` (the unbound signal-input default).
   */
  private _writeValueCalled: boolean = false;

  get hasValue(): boolean {
    return !!this._startDate() && !!this._endDate();
  }
}
